// Generated by TaroBase SDK Generator: 2026-05-01T17:36:41.367Z
// Collection: userPaymentAccounts/$userAccountId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Reusable per-user balance PDA. The $userAccountId is a non-wallet, deterministic id derived client-side as a hash of the user's wallet address. Creating the doc triggers @AccountPlugin.createAccount($userAccountId), which initializes the program-derived account on-chain (idempotent). The PDA holds reusable balances (SOL/USDC/USDT) that the user can later apply toward checkout sessions or withdraw. Queries resolve the actual base58 PDA public key and live token balances.
 */

export interface UserPaymentAccountsRequest {
  user: AddressType;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface UserPaymentAccountsResponse {
  user: string;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
  tarobase_transaction_hash?: string | undefined; // Represents the hash of the transaction that created this item. Can be used to create Solscan links (combine with the current network in the Tarobase app)
}

/**
 * Build a UserPaymentAccounts operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUserPaymentAccounts(userAccountId: string, data?: UserPaymentAccountsRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `userPaymentAccounts/${userAccountId}`, document: doc };
}

/**
 * Authenticated user creates their own balance PDA. Rule enforces @newData.user == @user.address so a user cannot register a PDA owned by someone else. The $userAccountId is a hash of the wallet address derived client-side — the create hook calls @AccountPlugin.createAccount($userAccountId), which is idempotent and safe to retry. Once created, this PDA serves as a reusable balance for deposits, settlements, and withdrawals. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setUserPaymentAccounts(userAccountId: string, data?: UserPaymentAccountsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`userPaymentAccounts/${userAccountId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting UserPaymentAccounts: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Public read. Anyone can read user payment account records — they only contain the owning wallet address and creation timestamp. Required for clients to look up the resolved base58 PDA and live balances via the queries.
   (Get Single Item)
 */
export async function getUserPaymentAccounts(userAccountId: string): Promise<UserPaymentAccountsResponse | null> {
  try {
    const result = await get(`userPaymentAccounts/${userAccountId}`);
    return result as UserPaymentAccountsResponse | null;
  } catch (error) {
    console.error(`Error getting UserPaymentAccounts: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single UserPaymentAccounts document. (
  Read Operation Details: Public read. Anyone can read user payment account records — they only contain the owning wallet address and creation timestamp. Required for clients to look up the resolved base58 PDA and live balances via the queries.
  )
 */
export function subscribeUserPaymentAccounts(callback: (data: UserPaymentAccountsResponse | null) => void, userAccountId: string): Promise<() => Promise<void>> {
  return subscribe(`userPaymentAccounts/${userAccountId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as UserPaymentAccountsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to UserPaymentAccounts at path '${`userPaymentAccounts/${userAccountId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many UserPaymentAccounts items from collection userPaymentAccounts
 
  Read Operation Details: Public read. Anyone can read user payment account records — they only contain the owning wallet address and creation timestamp. Required for clients to look up the resolved base58 PDA and live balances via the queries.
  
 */
export async function getManyUserPaymentAccounts(filter: string = ""): Promise<UserPaymentAccountsResponse[]> {
  try {
    const results = await get("userPaymentAccounts", { prompt: filter });
    return Array.isArray(results) ? results as UserPaymentAccountsResponse[] : [];
  } catch (error) {
    console.error(`Error getting UserPaymentAccounts collection at path 'userPaymentAccounts': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UserPaymentAccounts collection at userPaymentAccounts
 
  Read Operation Details: Public read. Anyone can read user payment account records — they only contain the owning wallet address and creation timestamp. Required for clients to look up the resolved base58 PDA and live balances via the queries.
  
 */
export function subscribeManyUserPaymentAccounts(callback: (data: UserPaymentAccountsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("userPaymentAccounts", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UserPaymentAccountsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UserPaymentAccounts collection at path '${"userPaymentAccounts"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all UserPaymentAccounts items from collection userPaymentAccounts
 
  Read Operation Details: Public read. Anyone can read user payment account records — they only contain the owning wallet address and creation timestamp. Required for clients to look up the resolved base58 PDA and live balances via the queries.
  
 */
export async function getAllUserPaymentAccounts(filter: string = ""): Promise<UserPaymentAccountsResponse[]> {
  try {
    const results = await get("userPaymentAccounts", { prompt: filter });
    return Array.isArray(results) ? results as UserPaymentAccountsResponse[] : [];
  } catch (error) {
    console.error(`Error getting UserPaymentAccounts collection at path 'userPaymentAccounts': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in UserPaymentAccounts collection at userPaymentAccounts
 
  Read Operation Details: Public read. Anyone can read user payment account records — they only contain the owning wallet address and creation timestamp. Required for clients to look up the resolved base58 PDA and live balances via the queries.
  
 */
export function subscribeAllUserPaymentAccounts(callback: (data: UserPaymentAccountsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("userPaymentAccounts", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UserPaymentAccountsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to UserPaymentAccounts collection at path '${"userPaymentAccounts"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count UserPaymentAccounts items in collection userPaymentAccounts.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countUserPaymentAccounts(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("userPaymentAccounts", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting UserPaymentAccounts at path 'userPaymentAccounts': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on UserPaymentAccounts items in collection userPaymentAccounts.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateUserPaymentAccounts(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("userPaymentAccounts", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating UserPaymentAccounts at path 'userPaymentAccounts': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Runs the "getPdaAddress" query on UserPaymentAccounts.
 * Query Logic: @AccountPlugin.getAccountAddress($userAccountId)
 */
export async function runGetPdaAddressQueryForUserPaymentAccounts(userAccountId: string, ): Promise<string> {
  try {
    const result = await runQuery(`userPaymentAccounts/${userAccountId}`, "getPdaAddress", {});
    return result as string;
  } catch (error) {
    console.error(`Error running query "getPdaAddress" on UserPaymentAccounts: ${formatError(error)}`);
    throw error;
  }
}

/**
 * Runs the "getSolBalance" query on UserPaymentAccounts.
 * Query Logic: @TokenPlugin.getBalance(@AccountPlugin.getAccountAddress($userAccountId), @constants.SOL)
 */
export async function runGetSolBalanceQueryForUserPaymentAccounts(userAccountId: string, ): Promise<number> {
  try {
    const result = await runQuery(`userPaymentAccounts/${userAccountId}`, "getSolBalance", {});
    return result as number;
  } catch (error) {
    console.error(`Error running query "getSolBalance" on UserPaymentAccounts: ${formatError(error)}`);
    throw error;
  }
}

/**
 * Runs the "getUsdcBalance" query on UserPaymentAccounts.
 * Query Logic: @TokenPlugin.getBalance(@AccountPlugin.getAccountAddress($userAccountId), @constants.USDC_MINT)
 */
export async function runGetUsdcBalanceQueryForUserPaymentAccounts(userAccountId: string, ): Promise<number> {
  try {
    const result = await runQuery(`userPaymentAccounts/${userAccountId}`, "getUsdcBalance", {});
    return result as number;
  } catch (error) {
    console.error(`Error running query "getUsdcBalance" on UserPaymentAccounts: ${formatError(error)}`);
    throw error;
  }
}

/**
 * Runs the "getUsdtBalance" query on UserPaymentAccounts.
 * Query Logic: @TokenPlugin.getBalance(@AccountPlugin.getAccountAddress($userAccountId), @constants.USDT_MINT)
 */
export async function runGetUsdtBalanceQueryForUserPaymentAccounts(userAccountId: string, ): Promise<number> {
  try {
    const result = await runQuery(`userPaymentAccounts/${userAccountId}`, "getUsdtBalance", {});
    return result as number;
  } catch (error) {
    console.error(`Error running query "getUsdtBalance" on UserPaymentAccounts: ${formatError(error)}`);
    throw error;
  }
}

